<script setup>
// M26.2.1 (round 2) — lightweight preview of the account-wide task queue
// (pending + running rows from workforce_tasks). Nexceo::Ai::WorkforceTaskQueue
// has no live callers yet (see the M26.2.1 audit), so this table is honestly
// empty today rather than padded with fake rows. `categoryCounts` — real
// last-24h runtime activity grouped by conversation/document/meeting —
// gives genuine signal in the meantime, per that audit's Part 7 guidance.
defineProps({
  tasks: { type: Array, required: true },
  categoryCounts: { type: Array, default: () => [] },
  viewAllTo: { type: Object, required: true },
});

const STATUS_CLASSES = {
  pending: 'bg-n-alpha-2 text-n-slate-8 border-n-weak',
  running:
    'bg-woot-50 text-woot-700 border-woot-200 dark:bg-woot-500/10 dark:text-woot-400 dark:border-woot-500/30',
};

function statusClass(status) {
  return STATUS_CLASSES[status] || STATUS_CLASSES.pending;
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="rounded-xl border border-n-weak bg-n-background overflow-hidden">
    <div
      class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
    >
      <h2
        class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider flex items-center gap-1.5"
      >
        <span class="i-lucide-list-todo size-3.5" />
        Task Queue
        <span
          v-if="tasks.length"
          class="text-[11px] px-1.5 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-8 normal-case font-normal"
        >
          {{ tasks.length }}
        </span>
      </h2>
      <router-link
        :to="viewAllTo"
        class="text-xs text-woot-600 dark:text-woot-400 hover:underline"
      >
        View all
      </router-link>
    </div>

    <!-- Real last-24h activity, grouped — shown even when the literal task
         queue below is empty, since it's genuine runtime signal. -->
    <div
      v-if="categoryCounts.length"
      class="flex items-center gap-4 px-4 py-2.5 border-b border-n-weak text-xs text-n-slate-9"
    >
      <span
        v-for="c in categoryCounts"
        :key="c.label"
        class="flex items-center gap-1.5"
      >
        <span :class="c.icon" class="size-3.5 text-n-slate-7" />
        <span class="font-semibold tabular-nums">{{ c.value }}</span>
        {{ c.label }}
      </span>
    </div>

    <div v-if="!tasks.length" class="flex flex-col items-center py-5 gap-1">
      <span class="i-lucide-inbox size-5 text-n-slate-6" />
      <p class="text-xs text-n-slate-7">Queue is empty — no pending work.</p>
    </div>

    <div v-else class="divide-y divide-n-weak">
      <div
        v-for="task in tasks"
        :key="task.id"
        class="flex items-center gap-3 px-4 py-2.5"
      >
        <span
          :class="
            task.status === 'running'
              ? 'animate-spin i-lucide-loader-2 text-woot-500'
              : 'i-lucide-clock text-n-slate-7'
          "
          class="size-3.5 shrink-0"
        />
        <div class="flex-1 min-w-0">
          <p class="text-xs font-medium text-n-slate-12 truncate capitalize">
            {{ task.task_type?.replace(/_/g, ' ') }}
          </p>
          <p class="text-[11px] text-n-slate-7 truncate">
            {{ task.lead_name || 'Unassigned' }}
            <template v-if="task.project_name">
              · {{ task.project_name }}
            </template>
          </p>
        </div>
        <span
          class="text-[11px] px-1.5 py-0.5 rounded-full font-medium border capitalize shrink-0"
          :class="statusClass(task.status)"
        >
          {{ task.status }}
        </span>
      </div>
    </div>
  </div>
</template>
